import { Button, CustomSelect, Search, Spinner, type SelectOption } from '@consigli/facade';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';

import { HintBox } from '../components';
import { useOutlookCalendar } from '../hooks';
import { DateRangePopover } from './date-range-popover';
import { OutlookMeeting, OutlookRange, type OutlookImportSelection } from './outlook-import.types';
import { OutlookMeetingRow } from './outlook-meeting-row';

interface OutlookImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (selection: OutlookImportSelection) => Promise<void> | void;
  isImporting?: boolean;
  /** Graph event ids already imported into this project — shown disabled. */
  existingCalendarEventIds?: Set<string>;
}

const resolveRange = (range: OutlookRange): { startDateTime: string; endDateTime: string } => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (range) {
    case OutlookRange.Today: {
      const end = new Date(startOfToday);
      end.setHours(23, 59, 59, 999);
      return { startDateTime: startOfToday.toISOString(), endDateTime: end.toISOString() };
    }
    case OutlookRange.Past30: {
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - 30);
      const end = new Date(startOfToday);
      end.setHours(23, 59, 59, 999);
      return { startDateTime: start.toISOString(), endDateTime: end.toISOString() };
    }
    case OutlookRange.Next7:
    case OutlookRange.Next30: {
      const days = range === OutlookRange.Next7 ? 7 : 30;
      const end = new Date(startOfToday);
      end.setDate(end.getDate() + days);
      end.setHours(23, 59, 59, 999);
      return { startDateTime: now.toISOString(), endDateTime: end.toISOString() };
    }
    case OutlookRange.ThisMonth:
    default: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { startDateTime: start.toISOString(), endDateTime: end.toISOString() };
    }
  }
};

const formatDayHeader = (startMs: number, locale: string): string =>
  new Date(startMs)
    .toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
    .toUpperCase();

export const OutlookImportModal = ({
  isOpen,
  onClose,
  onImport,
  isImporting = false,
  existingCalendarEventIds,
}: OutlookImportModalProps) => {
  const { t, i18n } = useTranslation();

  const [range, setRange] = useState<OutlookRange>(OutlookRange.ThisMonth);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [searchText, setSearchText] = useState('');
  const [organizerFilter, setOrganizerFilter] = useState('');
  const [selectedMeetings, setSelectedMeetings] = useState<Map<string, OutlookMeeting>>(new Map());

  const [customCommitted, setCustomCommitted] = useState<{
    startDateTime: string;
    endDateTime: string;
  } | null>(null);

  const activeRange = useMemo(() => {
    if (range === OutlookRange.Custom) return customCommitted;
    return resolveRange(range);
  }, [range, customCommitted]);

  const { meetings, isLoading, error } = useOutlookCalendar({
    startDateTime: activeRange?.startDateTime ?? '',
    endDateTime: activeRange?.endDateTime ?? '',
    enabled: isOpen && activeRange !== null,
  });

  const handleCustomRange = (start: string, end: string) => {
    setRange(OutlookRange.Custom);
    setCustomStart(start);
    setCustomEnd(end);
    setCustomCommitted({
      startDateTime: new Date(`${start}T00:00:00`).toISOString(),
      endDateTime: new Date(`${end}T23:59:59.999`).toISOString(),
    });
  };

  const handleRangeSelect = (value: OutlookRange) => setRange(value);

  // Non-cancelled meetings are the working set; everything counts against these.
  const activeMeetings = useMemo(() => meetings.filter((m) => !m.isCancelled), [meetings]);

  const organizerOptions = useMemo<SelectOption[]>(() => {
    const byEmail = new Map<string, string>();
    for (const m of activeMeetings) {
      if (m.organizerEmail && !byEmail.has(m.organizerEmail)) {
        byEmail.set(m.organizerEmail, m.organizerName || m.organizerEmail);
      }
    }
    return [
      { value: '', label: t('meeting-secretary.outlook-import.all-organizers') },
      ...Array.from(byEmail.entries()).map(([email, name]) => ({ value: email, label: name })),
    ];
  }, [activeMeetings, t]);

  const filteredMeetings = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return activeMeetings.filter((m) => {
      if (organizerFilter && m.organizerEmail !== organizerFilter) {
        return false;
      }
      if (search) {
        const haystack = [m.title, ...m.attendeeEmails].join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [activeMeetings, searchText, organizerFilter]);

  // Group by local day, preserving chronological order.
  const groups = useMemo(() => {
    const sorted = [...filteredMeetings].sort((a, b) => a.startMs - b.startMs);
    const map = new Map<string, OutlookMeeting[]>();
    for (const m of sorted) {
      const key = formatDayHeader(m.startMs, i18n.language);
      const bucket = map.get(key);
      if (bucket) bucket.push(m);
      else map.set(key, [m]);
    }
    return Array.from(map.entries());
  }, [filteredMeetings, i18n.language]);

  const isAlreadyImported = (m: OutlookMeeting) => Boolean(existingCalendarEventIds?.has(m.id));

  const toggleSelect = (meeting: OutlookMeeting) => {
    setSelectedMeetings((prev) => {
      const next = new Map(prev);
      if (next.has(meeting.id)) next.delete(meeting.id);
      else next.set(meeting.id, meeting);
      return next;
    });
  };

  const handleClose = () => {
    setSelectedMeetings(new Map());
    setSearchText('');
    setRange(OutlookRange.ThisMonth);
    setCustomCommitted(null);
    onClose();
  };

  const handleImport = () => {
    const eventIds: string[] = [];
    const seriesIds = new Set<string>();
    for (const m of selectedMeetings.values()) {
      if (m.seriesMasterId) seriesIds.add(m.seriesMasterId);
      else eventIds.push(m.id);
    }
    void onImport({ eventIds, seriesIds: Array.from(seriesIds) });
    setSelectedMeetings(new Map());
  };

  if (!isOpen) return null;

  const rangeChips: { value: OutlookRange; label: string }[] = [
    {
      value: OutlookRange.ThisMonth,
      label: t('meeting-secretary.outlook-import.range-this-month'),
    },
    { value: OutlookRange.Today, label: t('meeting-secretary.outlook-import.range-today') },
    { value: OutlookRange.Past30, label: t('meeting-secretary.outlook-import.range-past-30') },
    { value: OutlookRange.Next7, label: t('meeting-secretary.outlook-import.range-next-7') },
    { value: OutlookRange.Next30, label: t('meeting-secretary.outlook-import.range-next-30') },
  ];

  const selectedOrganizer = organizerOptions.find((o) => o.value === organizerFilter);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleClose();
      }}
      role="button"
      tabIndex={0}
      aria-label={t('meeting-secretary.outlook-import.close')}
    >
      <div className="flex h-full items-center justify-center p-8">
        <div
          className="flex max-h-[85vh] w-[70vw] min-w-[400px] max-w-[900px] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="outlook-import-title"
        >
          {/* Header */}
          <div className="flex flex-shrink-0 items-start justify-between p-6 pb-4">
            <div>
              <h2 id="outlook-import-title" className="mb-1 text-xl font-semibold text-gray-900">
                {t('meeting-secretary.outlook-import.title')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('meeting-secretary.outlook-import.subtitle')}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('meeting-secretary.outlook-import.close')}
            >
              <FaTimes size={18} />
            </button>
          </div>

          <HintBox className="mx-6 mb-4 flex-shrink-0">
            {t('meeting-secretary.outlook-import.recording-note')}
          </HintBox>

          {/* Filter bar */}
          <div className="flex flex-shrink-0 flex-col gap-3 border-y border-gray-200 bg-gray-50 px-6 py-4">
            <Search
              text={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('meeting-secretary.outlook-import.search-placeholder')}
              className="w-full"
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {rangeChips.map((chip) => (
                  <Button
                    key={chip.value}
                    {...(range === chip.value ? { primary: true } : { secondary: true })}
                    rounded
                    onClick={() => handleRangeSelect(chip.value)}
                  >
                    {chip.label}
                  </Button>
                ))}
                <DateRangePopover
                  startDate={customStart}
                  endDate={customEnd}
                  onSelect={handleCustomRange}
                  active={range === OutlookRange.Custom}
                  label={t('meeting-secretary.outlook-import.range-custom')}
                  locale={i18n.language}
                />
              </div>
              <div className="w-56 flex-shrink-0">
                <CustomSelect
                  options={organizerOptions}
                  value={selectedOrganizer ? [selectedOrganizer] : []}
                  onChange={(opts) => setOrganizerFilter(String(opts[0]?.value ?? ''))}
                  placeholder={t('meeting-secretary.outlook-import.organizer-placeholder')}
                  isClearable
                />
              </div>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner />
              </div>
            ) : error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            ) : groups.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                {t('meeting-secretary.outlook-import.no-meetings')}
              </div>
            ) : (
              <div className="space-y-6">
                {groups.map(([day, dayMeetings]) => (
                  <div key={day}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {day}
                    </h3>
                    <div className="space-y-3">
                      {dayMeetings.map((meeting) => (
                        <OutlookMeetingRow
                          key={meeting.id}
                          meeting={meeting}
                          selected={selectedMeetings.has(meeting.id)}
                          disabled={isImporting || isAlreadyImported(meeting)}
                          onToggle={() => toggleSelect(meeting)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-shrink-0 items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <span className="text-sm text-gray-500">
              {t('meeting-secretary.outlook-import.showing-count', {
                shown: filteredMeetings.length,
                total: activeMeetings.length,
              })}
            </span>
            <div className="flex items-center gap-3">
              <Button secondary rounded onClick={handleClose} disabled={isImporting} type="button">
                {t('meeting-secretary.outlook-import.cancel')}
              </Button>
              <Button
                primary
                rounded
                onClick={handleImport}
                disabled={isImporting || selectedMeetings.size === 0}
                type="button"
              >
                {isImporting
                  ? t('meeting-secretary.outlook-import.importing')
                  : t('meeting-secretary.outlook-import.add-selected', {
                      count: selectedMeetings.size,
                    })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
